import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  AppBar, Avatar, Box, Chip, Drawer, IconButton, List, ListItemButton,
  ListItemIcon, ListItemText, Menu, MenuItem, Toolbar, Typography, useMediaQuery,
} from "@mui/material";
import { useState } from "react";
import ApartmentIcon from "@mui/icons-material/Apartment";
import DashboardIcon from "@mui/icons-material/Dashboard";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import HistoryIcon from "@mui/icons-material/History";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import EventNoteIcon from "@mui/icons-material/EventNote";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import TuneIcon from "@mui/icons-material/Tune";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import TranslateIcon from "@mui/icons-material/Translate";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { useLanguage } from "../i18n/LanguageContext";
import { LANGS } from "../i18n/translations";
import { COLORS } from "../theme";
import { navPalette } from "../utils/navColor";

const DRAWER_WIDTH = 240;

// Definido a nivel de módulo (no dentro de Layout): un componente definido
// dentro de otra función se recrea como un tipo distinto en cada render,
// lo que puede provocar que React desmonte/remonte su árbol de forma
// inesperada — una causa real de errores intermitentes al navegar.
function LanguageSwitcher({ color, lang, setLang }) {
  const [anchor, setAnchor] = useState(null);
  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)} sx={{ color: color || "inherit" }}>
        <TranslateIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {LANGS.map((l) => (
          <MenuItem
            key={l.code}
            selected={l.code === lang}
            onClick={() => {
              setLang(l.code);
              setAnchor(null);
            }}
          >
            {l.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default function Layout() {
  const { user, logout, isAdmin } = useAuth();
  const { settings } = useSettings();
  const { t, lang, setLang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width:760px)");
  const [mobileOpen, setMobileOpen] = useState(false);

  // Color de fondo de la barra de navegación: configurable desde Ajustes
  // (admin), con el ink de siempre como default si no se ha personalizado.
  const navBg = settings?.sidebarColor || COLORS.ink;
  const nav = navPalette(navBg, COLORS);

  const navItems = [
    { to: "/panel", label: t("nav.panel"), icon: <DashboardIcon /> },
    { to: "/registrar", label: t("nav.registrar"), icon: <Inventory2Icon /> },
    { to: "/visitantes", label: t("nav.visitantes"), icon: <HowToRegIcon /> },
    ...(isAdmin ? [{ to: "/directorio", label: t("nav.directorio"), icon: <PeopleAltIcon /> }] : []),
    { to: "/historial", label: t("nav.historial"), icon: <HistoryIcon /> },
    ...(isAdmin ? [{ to: "/usuarios", label: t("nav.usuarios"), icon: <ManageAccountsIcon /> }] : []),
    ...(isAdmin ? [{ to: "/accesos", label: t("nav.accesos"), icon: <FactCheckIcon /> }] : []),
    ...(isAdmin ? [{ to: "/areas-comunes", label: t("nav.areasComunes"), icon: <EventNoteIcon /> }] : []),
    ...(isAdmin ? [{ to: "/ajustes", label: t("nav.ajustes"), icon: <TuneIcon /> }] : []),
  ];

  // La línea bajo el nombre del condominio refleja la sección actual en
  // vez de un lema fijo.
  const currentSectionLabel = navItems.find((item) => location.pathname.startsWith(item.to))?.label || t("layout.tagline");

  const brandIcon = settings?.logo ? (
    <Avatar variant="rounded" src={settings.logo} sx={{ width: 28, height: 28 }} />
  ) : (
    <ApartmentIcon sx={{ color: COLORS.brass }} />
  );

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: navBg, color: nav.textPrimary }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, p: 2.5, pb: 1 }}>
        {brandIcon}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} lineHeight={1.1} noWrap>
            {settings?.condoName || t("common.loading")}
          </Typography>
          <Typography variant="caption" sx={{ color: nav.textMuted }} noWrap>
            {currentSectionLabel}
          </Typography>
        </Box>
        <LanguageSwitcher color={nav.navInactive} lang={lang} setLang={setLang} />
      </Box>

      <List sx={{ px: 1.5, mt: 1, flex: 1 }}>
        {navItems.map((item) => (
          <ListItemButton
            key={item.to}
            component={NavLink}
            to={item.to}
            onClick={() => setMobileOpen(false)}
            sx={{
              borderRadius: 1.5,
              mb: 0.5,
              color: nav.navInactive,
              borderLeft: "2px solid transparent",
              "&:hover": { bgcolor: nav.hoverBg },
              "&.active": {
                bgcolor: nav.activeBg,
                color: nav.activeText,
                borderLeftColor: COLORS.brass,
              },
            }}
          >
            <ListItemIcon sx={{ color: "inherit", minWidth: 36 }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItemButton>
        ))}
      </List>

      <Box sx={{ p: 2, borderTop: `1px solid ${nav.borderColor}` }}>
        <Typography variant="body2" fontWeight={600}>
          {user?.name}
        </Typography>
        <Chip
          label={t(`layout.role_${user?.role}`)}
          size="small"
          sx={{ mt: 0.5, mb: 1.5, bgcolor: nav.chipBg, color: nav.activeText, fontSize: 11 }}
        />
        <ListItemButton
          onClick={() => {
            logout();
            navigate("/login");
          }}
          sx={{ borderRadius: 1.5, bgcolor: nav.logoutBg, color: nav.navInactive }}
        >
          <ListItemIcon sx={{ color: "inherit", minWidth: 36 }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t("layout.logout")} primaryTypographyProps={{ fontSize: 13 }} />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: COLORS.paper }}>
      {isMobile && (
        <AppBar position="fixed" sx={{ bgcolor: navBg, color: nav.textPrimary }}>
          <Toolbar variant="dense">
            <IconButton edge="start" onClick={() => setMobileOpen(true)} sx={{ color: nav.textPrimary }}>
              <MenuIcon />
            </IconButton>
            <Box sx={{ ml: 1, flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" noWrap>
                {settings?.condoName || "Recepción"}
              </Typography>
              <Typography variant="caption" sx={{ color: nav.textMuted }} noWrap>
                {currentSectionLabel}
              </Typography>
            </Box>
            <LanguageSwitcher color={nav.textPrimary} lang={lang} setLang={setLang} />
          </Toolbar>
        </AppBar>
      )}

      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={() => setMobileOpen(false)}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: { width: DRAWER_WIDTH, boxSizing: "border-box", border: "none" },
        }}
      >
        {drawerContent}
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, md: 4 }, mt: isMobile ? 6 : 0, maxWidth: 1200 }}>
        <Outlet />
      </Box>
    </Box>
  );
}
